import { useState, useRef, useEffect } from "react";
import {
  X,
  Edit3,
  ArrowRightLeft,
  Lock,
  Unlock,
  Users,
  Clock,
  Phone,
  Check,
  CheckCircle,
  LogIn,
  UserCheck,
  XCircle,
  AlertCircle,
  Footprints,
  ChevronLeft,
  Settings,
} from "lucide-react";
import {
  colorProps,
  statusBadgeColorProps,
  tableStatusColorProps,
  statusButtonColorProps,
} from "../lib/status-colors";

// Types - simplified to match what we actually need
interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
}

interface Table {
  id: string;
  name: string;
  minCapacity: number;
  maxCapacity: number;
  shape?: string;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  roomId?: string;
}

interface Reservation {
  id: string;
  date: string;
  time: string;
  partySize: number;
  status: string;
  source?: string;
  notes?: string | null;
  internalNotes?: string | null;
  menuSelections?: { quantity: number; menu: { name: string } }[] | null;
  customerId?: string;
  tableId?: string | null;
  tableCombinationId?: string | null;
  customer: Customer;
  table?: Table | null;
  tableCombination?: {
    id: string;
    name: string;
    tables: {
      id?: string;
      table: {
        id: string;
        name: string;
      };
    }[];
  } | null;
}

interface TableBlock {
  id: string;
  tableId: string;
  date: string;
  shift: string | null;
  reason: string | null;
}

interface TableCapacityOverride {
  id: string;
  tableId: string;
  date: string;
  shift: string | null; // "comida" | "cena" | null (null = todo el día)
  minCapacity: number;
  maxCapacity: number;
  reason: string | null;
}

interface ReservationStatusConfig {
  id: string;
  code: string;
  name: string;
  color: string;
  bgColor: string;
  textColor: string;
  icon: string | null;
  isFinal: boolean;
}

interface TablePopoverProps {
  table: Table;
  reservation: Reservation | null;
  tableReservations?: Reservation[];
  block: TableBlock | null;
  capacityOverride: TableCapacityOverride | null;
  position: { x: number; y: number };
  containerRef: React.RefObject<HTMLDivElement | null>;
  availableStatuses: ReservationStatusConfig[];
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEditReservation: (reservation: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMoveTable: (reservation: any) => void;
  onReleaseTable: (reservation: any) => void;
  onChangeStatus: (reservationId: string, status: string) => void;
  onBlockTable: (tableId: string) => void;
  onUnblockTable: (tableId: string, blockId: string) => void;
  onSetCapacityOverride: (
    tableId: string,
    data: {
      minCapacity: number;
      maxCapacity: number;
      shift: "comida" | "cena" | null;
      reason?: string;
    },
  ) => void;
  onRemoveCapacityOverride: (overrideId: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onNewReservation: (table: any) => void;
  onWalkIn: (tableId: string, partySize: number) => void;
}

// Icon mapping
const ICON_MAP: Record<string, React.ElementType> = {
  Clock,
  Check,
  CheckCircle,
  LogIn,
  UserCheck,
  X,
  XCircle,
  AlertCircle,
};

export default function TablePopover({
  table,
  reservation,
  tableReservations = [],
  block,
  capacityOverride,
  position,
  containerRef,
  availableStatuses,
  onClose,
  onEditReservation,
  onMoveTable,
  onReleaseTable,
  onChangeStatus,
  onBlockTable,
  onUnblockTable,
  onSetCapacityOverride,
  onRemoveCapacityOverride,
  onNewReservation,
  onWalkIn,
}: TablePopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  // Arranca fuera de pantalla: se coloca tras medirse (evita el parpadeo en
  // la esquina mientras aún no se conoce el tamaño del panel).
  const [adjustedPosition, setAdjustedPosition] = useState({
    x: -9999,
    y: -9999,
  });

  // Capacity override mini-form state
  const [showCapacityForm, setShowCapacityForm] = useState(false);
  const [capacityMinInput, setCapacityMinInput] = useState<number | "">(
    table.minCapacity,
  );
  const [capacityMaxInput, setCapacityMaxInput] = useState<number | "">(
    table.maxCapacity,
  );
  const [capacityShiftInput, setCapacityShiftInput] = useState<
    "" | "comida" | "cena"
  >("");
  const [capacityReasonInput, setCapacityReasonInput] = useState("");
  const [capacityFormError, setCapacityFormError] = useState<string | null>(
    null,
  );

  // Open the mini-form, pre-filling it with the current override (if any)
  const openCapacityForm = () => {
    setCapacityMinInput(capacityOverride?.minCapacity ?? table.minCapacity);
    setCapacityMaxInput(capacityOverride?.maxCapacity ?? table.maxCapacity);
    setCapacityShiftInput(
      capacityOverride
        ? ((capacityOverride.shift as "comida" | "cena" | null) ?? "")
        : "",
    );
    setCapacityReasonInput(capacityOverride?.reason ?? "");
    setCapacityFormError(null);
    setShowCapacityForm(true);
  };

  // Allow clearing the input while editing; coercion/validation happens on save
  // (same pattern as TableMap.tsx's handleCapacityInputChange)
  const handleCapacityInputChange = (field: "min" | "max", rawValue: string) => {
    if (rawValue === "") {
      if (field === "min") setCapacityMinInput("");
      else setCapacityMaxInput("");
      return;
    }
    const parsed = parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) return;
    if (field === "min") setCapacityMinInput(parsed);
    else setCapacityMaxInput(parsed);
  };

  const handleSaveCapacityOverride = () => {
    if (capacityMinInput === "" || capacityMaxInput === "") {
      setCapacityFormError("Indica el aforo mínimo y máximo");
      return;
    }
    if (capacityMinInput < 1 || capacityMaxInput < 1) {
      setCapacityFormError("El aforo debe ser de al menos 1 persona");
      return;
    }
    if (capacityMinInput > capacityMaxInput) {
      setCapacityFormError("El aforo mínimo no puede ser mayor que el máximo");
      return;
    }
    setCapacityFormError(null);
    onSetCapacityOverride(table.id, {
      minCapacity: capacityMinInput,
      maxCapacity: capacityMaxInput,
      shift: capacityShiftInput === "" ? null : capacityShiftInput,
      reason: capacityReasonInput.trim() || undefined,
    });
  };

  const handleRemoveCurrentOverride = () => {
    if (!capacityOverride) return;
    onRemoveCapacityOverride(capacityOverride.id);
  };

  // Walk-in mini-form state (nº de personas exacto, en vez de asumir la
  // capacidad máxima de la mesa). La hora la pone el backend (hora real).
  const [showWalkInForm, setShowWalkInForm] = useState(false);
  const [walkInPartySizeInput, setWalkInPartySizeInput] = useState<
    number | ""
  >(table.minCapacity || 2);
  const [walkInFormError, setWalkInFormError] = useState<string | null>(null);

  const openWalkInForm = () => {
    setWalkInPartySizeInput(table.minCapacity || 2);
    setWalkInFormError(null);
    setShowWalkInForm(true);
  };

  // Allow clearing the input while editing; coercion/validation happens on
  // confirm (same pattern as the capacity mini-form above)
  const handleWalkInInputChange = (rawValue: string) => {
    if (rawValue === "") {
      setWalkInPartySizeInput("");
      return;
    }
    const parsed = parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) return;
    setWalkInPartySizeInput(parsed);
  };

  const handleWalkInStep = (delta: number) => {
    setWalkInPartySizeInput((prev) => {
      const current = prev === "" ? 0 : prev;
      const next = current + delta;
      return next < 1 ? 1 : next;
    });
  };

  const handleConfirmWalkIn = () => {
    if (walkInPartySizeInput === "") {
      setWalkInFormError("Indica el número de personas");
      return;
    }
    if (
      !Number.isInteger(walkInPartySizeInput) ||
      walkInPartySizeInput < 1
    ) {
      setWalkInFormError(
        "El número de personas debe ser un entero de al menos 1",
      );
      return;
    }
    setWalkInFormError(null);
    onWalkIn(table.id, walkInPartySizeInput);
  };

  // Internal state: which reservation is "active" (selected from chooser or single)
  const [activeReservation, setActiveReservation] =
    useState<Reservation | null>(() => {
      // Multiple reservations on this table → show chooser first
      if (tableReservations.length > 1) return null;
      // Single reservation passed directly
      if (tableReservations.length === 1) return tableReservations[0];
      return reservation;
    });

  // Status color helper for chooser items. Usa los colores REALES configurados por el
  // superadmin (`availableStatuses`, bgColor/textColor — tolera Tailwind y HEX) en vez de un
  // mapa hardcodeado. El "container" (fondo+borde de la fila) y el "badge" (pill de la
  // etiqueta) reutilizan bgColor/textColor tal cual; el "dot" (punto sólido) reutiliza
  // textColor como relleno, ya que es el tono más saturado disponible.
  const getStatusColors = (status: string) => {
    const dynamicStatus = availableStatuses.find((s) => s.code === status);
    if (!dynamicStatus) {
      return {
        container: { className: "bg-gray-50 border-gray-300", style: {} },
        badge: { className: "bg-gray-50 text-gray-600", style: {} },
        dot: { className: "bg-gray-400", style: {} },
      };
    }
    return {
      container: tableStatusColorProps(
        dynamicStatus.bgColor,
        dynamicStatus.textColor,
      ),
      badge: statusBadgeColorProps(
        dynamicStatus.bgColor,
        dynamicStatus.textColor,
      ),
      dot: colorProps(dynamicStatus.textColor, "backgroundColor"),
    };
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Posicionamiento en coordenadas de PANTALLA (el panel es `fixed`): antes se
  // ajustaba solo al recuadro del mapa y quedaba cortado en móvil o escondido
  // en la zona con scroll del escritorio. Ahora:
  //  - móvil (<768px): centrado en pantalla, siempre visible entero;
  //  - escritorio: junto a la mesa pulsada, recortado a los bordes del
  //    viewport (y volteado hacia arriba si no cabe por abajo).
  // El ResizeObserver recoloca si el panel cambia de tamaño al cambiar de
  // vista (lista → acciones → bloqueo…).
  useEffect(() => {
    const reposition = () => {
      const el = popoverRef.current;
      const container = containerRef.current;
      if (!el || !container) return;
      const panel = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      if (vw < 768) {
        setAdjustedPosition({
          x: Math.max(8, (vw - panel.width) / 2),
          y: Math.max(8, (vh - panel.height) / 2),
        });
        return;
      }

      const containerRect = container.getBoundingClientRect();
      let x = containerRect.left + position.x;
      let y = containerRect.top + position.y;
      if (x + panel.width > vw - 8) x = Math.max(8, vw - panel.width - 8);
      if (y + panel.height > vh - 8) {
        y = Math.max(8, Math.min(y - panel.height - 10, vh - panel.height - 8));
      }
      if (y < 8) y = 8;
      setAdjustedPosition({ x, y });
    };

    reposition();
    const el = popoverRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(reposition);
    observer.observe(el);
    return () => observer.disconnect();
  }, [position, containerRef]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // --- CHOOSER VIEW: multiple reservations on this table ---
  if (tableReservations.length > 1 && activeReservation === null) {
    return (
      <div
        ref={popoverRef}
        className="fixed z-[75] bg-white rounded-xl shadow-2xl border border-gray-200 w-72 max-w-[calc(100vw-16px)] max-h-[85dvh] overflow-y-auto overflow-x-hidden"
        style={{ left: adjustedPosition.x, top: adjustedPosition.y }}
      >
        {/* Header */}
        <div className="bg-orange-500 text-white px-4 py-3 flex items-center justify-between">
          <div>
            <span className="font-semibold text-sm">{table.name}</span>
            <p className="text-orange-100 text-xs mt-0.5">
              {tableReservations.length} reservas en este turno
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Reservation list */}
        <div className="p-2 space-y-2">
          {tableReservations.map((res) => {
            const sc = getStatusColors(res.status);
            const statusLabel =
              availableStatuses.find((s) => s.code === res.status)?.name ||
              res.status;
            return (
              <button
                key={res.id}
                onClick={() => setActiveReservation(res)}
                className={`w-full text-left rounded-lg border-2 px-3 py-2.5 transition-all hover:shadow-md hover:scale-[1.01] ${sc.container.className}`}
                style={sc.container.style}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sc.dot.className}`}
                      style={sc.dot.style}
                    />
                    <span className="font-bold text-gray-900 text-sm tabular-nums">
                      {res.time.slice(0, 5)}
                    </span>
                    <span className="font-medium text-gray-800 text-sm truncate">
                      {res.customer.firstName} {res.customer.lastName}
                    </span>
                  </div>
                  <span
                    className={`text-xs font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${sc.badge.className}`}
                    style={sc.badge.style}
                  >
                    {statusLabel}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 ml-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {res.partySize} pax
                  </span>
                  {(res.menuSelections?.length ?? 0) > 0 && (
                    <span
                      className="flex-shrink-0"
                      title={res
                        .menuSelections!.map(
                          (s) => `${s.quantity}× ${s.menu.name}`,
                        )
                        .join(" · ")}
                    >
                      🍽️{" "}
                      {res.menuSelections!.reduce(
                        (sum, s) => sum + s.quantity,
                        0,
                      )}
                    </span>
                  )}
                  {res.notes && (
                    <span className="flex-shrink-0" title={res.notes}>
                      📝
                    </span>
                  )}
                  {res.customer.phone && (
                    <span className="flex items-center gap-1 truncate">
                      <Phone className="w-3 h-3 flex-shrink-0" />
                      {res.customer.phone}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
          <p className="text-[10px] text-gray-400 text-center">
            Toca una reserva para ver acciones
          </p>
        </div>
      </div>
    );
  }

  // Blocked table view
  if (block) {
    return (
      <div
        ref={popoverRef}
        className="fixed z-[75] bg-white rounded-xl shadow-2xl border border-gray-200 w-64 max-w-[calc(100vw-16px)] max-h-[85dvh] overflow-y-auto overflow-x-hidden"
        style={{ left: adjustedPosition.x, top: adjustedPosition.y }}
      >
        {/* Header - Blocked */}
        <div className="bg-red-600 text-white p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span className="font-semibold">{table.name}</span>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 space-y-3">
          <div className="text-center py-2">
            <Lock className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Mesa Bloqueada</p>
            {block.reason && (
              <p className="text-xs text-gray-500 mt-1">{block.reason}</p>
            )}
            {block.shift && (
              <p className="text-xs text-gray-500 mt-1">
                Turno: {block.shift === "comida" ? "Comida" : "Cena"}
              </p>
            )}
          </div>

          {/* Unblock button */}
          <button
            onClick={() => onUnblockTable(table.id, block.id)}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
          >
            <Unlock className="w-4 h-4" />
            Desbloquear Mesa
          </button>
        </div>
      </div>
    );
  }

  // Capacity override mini-form (accesible desde "mesa libre" y "con reserva")
  if (showCapacityForm) {
    const selectedShiftValue =
      capacityShiftInput === "" ? null : capacityShiftInput;
    const overrideMatchesForm =
      !!capacityOverride &&
      (capacityOverride.shift ?? null) === selectedShiftValue;

    return (
      <div
        ref={popoverRef}
        className="fixed z-[75] bg-white rounded-xl shadow-2xl border border-gray-200 w-72 max-w-[calc(100vw-16px)] max-h-[85dvh] overflow-y-auto overflow-x-hidden"
        style={{ left: adjustedPosition.x, top: adjustedPosition.y }}
      >
        {/* Header */}
        <div className="bg-amber-500 text-white p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setShowCapacityForm(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
                title="Volver"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="min-w-0">
                <span className="font-semibold text-sm truncate block">
                  {table.name}
                </span>
                <p className="text-amber-100 text-xs">Ajustar aforo de hoy</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-3 space-y-3">
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">
              Turno
            </label>
            <select
              value={capacityShiftInput}
              onChange={(e) =>
                setCapacityShiftInput(
                  e.target.value as "" | "comida" | "cena",
                )
              }
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-400"
            >
              <option value="">Todo el día</option>
              <option value="comida">Comida</option>
              <option value="cena">Cena</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">
                Mínimo
              </label>
              <input
                type="number"
                min={1}
                max={50}
                value={capacityMinInput}
                onChange={(e) =>
                  handleCapacityInputChange("min", e.target.value)
                }
                className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">
                Máximo
              </label>
              <input
                type="number"
                min={1}
                max={50}
                value={capacityMaxInput}
                onChange={(e) =>
                  handleCapacityInputChange("max", e.target.value)
                }
                className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">
              Motivo (opcional)
            </label>
            <input
              type="text"
              value={capacityReasonInput}
              onChange={(e) => setCapacityReasonInput(e.target.value)}
              placeholder="Ej: evento privado, mesa reducida..."
              maxLength={500}
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          {capacityFormError && (
            <p className="text-xs text-red-600">{capacityFormError}</p>
          )}

          <p className="text-[11px] text-gray-400">
            Capacidad base de la mesa: {table.minCapacity}-{table.maxCapacity}{" "}
            personas.
          </p>

          <div className="flex gap-2 pt-1">
            {overrideMatchesForm && (
              <button
                onClick={handleRemoveCurrentOverride}
                className="flex-1 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors"
              >
                Quitar override
              </button>
            )}
            <button
              onClick={handleSaveCapacityOverride}
              className="flex-1 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Guardar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Walk-in mini-form (nº de personas exacto antes de sentar la mesa)
  if (showWalkInForm) {
    return (
      <div
        ref={popoverRef}
        className="fixed z-[75] bg-white rounded-xl shadow-2xl border border-gray-200 w-64 max-w-[calc(100vw-16px)] max-h-[85dvh] overflow-y-auto overflow-x-hidden"
        style={{ left: adjustedPosition.x, top: adjustedPosition.y }}
      >
        {/* Header */}
        <div className="bg-purple-600 text-white p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setShowWalkInForm(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
                title="Volver"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="min-w-0">
                <span className="font-semibold text-sm truncate block">
                  {table.name}
                </span>
                <p className="text-purple-100 text-xs">Walk-in</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-3 space-y-3">
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">
              Nº de personas
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleWalkInStep(-1)}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors"
                aria-label="Restar una persona"
              >
                −
              </button>
              <input
                type="number"
                min={1}
                max={50}
                value={walkInPartySizeInput}
                onChange={(e) => handleWalkInInputChange(e.target.value)}
                className="w-full text-center text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <button
                type="button"
                onClick={() => handleWalkInStep(1)}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors"
                aria-label="Sumar una persona"
              >
                +
              </button>
            </div>
          </div>

          {walkInFormError && (
            <p className="text-xs text-red-600">{walkInFormError}</p>
          )}

          <p className="text-[11px] text-gray-400">
            Capacidad de la mesa: {table.minCapacity}-{table.maxCapacity}{" "}
            personas.
          </p>

          <div className="flex gap-2 pt-1">
            <button
              onClick={() => setShowWalkInForm(false)}
              className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmWalkIn}
              className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Sentar walk-in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Reservation view — use activeReservation (from chooser) or the passed reservation
  // Must be computed BEFORE free-table check so that clicking in the chooser
  // shows the detail view even when `reservation` prop is null.
  const activeRes = activeReservation || reservation;
  const fromChooser = tableReservations.length > 1;

  // Free table view (only when truly no reservation is active/selected)
  if (!activeRes) {
    return (
      <div
        ref={popoverRef}
        className="fixed z-[75] bg-white rounded-xl shadow-2xl border border-gray-200 w-64 max-w-[calc(100vw-16px)] max-h-[85dvh] overflow-y-auto overflow-x-hidden"
        style={{ left: adjustedPosition.x, top: adjustedPosition.y }}
      >
        {/* Header - Free */}
        <div className="bg-gray-100 p-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-900">{table.name}</span>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Capacidad:{" "}
            {capacityOverride
              ? `${capacityOverride.minCapacity}-${capacityOverride.maxCapacity}`
              : `${table.minCapacity}-${table.maxCapacity}`}{" "}
            personas
            {capacityOverride && (
              <span className="ml-1 text-amber-600 font-medium">
                (ajustado hoy)
              </span>
            )}
          </p>
        </div>

        {/* Content */}
        <div className="p-3 space-y-2">
          <p className="text-center text-sm text-gray-500 py-1">
            Mesa disponible
          </p>

          {/* Action buttons */}
          <div className="space-y-2">
            <button
              onClick={openWalkInForm}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Footprints className="w-4 h-4" />
              Walk-in
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => onNewReservation(table)}
                className="flex items-center justify-center gap-1.5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Users className="w-4 h-4" />
                Reservar
              </button>
              <button
                onClick={() => onBlockTable(table.id)}
                className="flex items-center justify-center gap-1.5 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors"
              >
                <Lock className="w-4 h-4" />
                Bloquear
              </button>
            </div>
            <button
              onClick={openCapacityForm}
              className="w-full flex items-center justify-center gap-1.5 py-2 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-sm font-medium transition-colors"
            >
              <Settings className="w-4 h-4" />
              Ajustar aforo hoy
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Reservation view
  const statusConfig = availableStatuses.find(
    (s) => s.code === activeRes.status,
  );
  const activeStatusBadgeColor = statusBadgeColorProps(
    statusConfig?.bgColor || "bg-gray-100",
    statusConfig?.textColor || "text-gray-700",
  );

  return (
    <div
      ref={popoverRef}
      className="fixed z-[75] bg-white rounded-xl shadow-2xl border border-gray-200 w-72 max-w-[calc(100vw-16px)] max-h-[85dvh] overflow-y-auto overflow-x-hidden"
      style={{ left: adjustedPosition.x, top: adjustedPosition.y }}
    >
      {/* Header with customer info */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-3">
        <div className="flex items-center gap-2">
          {/* Back button when coming from chooser */}
          {fromChooser && (
            <button
              onClick={() => setActiveReservation(null)}
              className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
              title="Ver todas las reservas"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold truncate">
              {activeRes.customer.firstName} {activeRes.customer.lastName}
            </h3>
            <div className="flex items-center gap-3 text-sm text-primary-100 mt-0.5">
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {activeRes.time.slice(0, 5)}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {activeRes.partySize} pax
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Quick info */}
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 text-xs text-gray-600">
        <div className="flex items-center justify-between">
          <span>
            {table.name}
            {capacityOverride && (
              <span className="ml-1.5 text-amber-600 font-medium">
                · Aforo hoy: {capacityOverride.minCapacity}-
                {capacityOverride.maxCapacity}
              </span>
            )}
          </span>
          {activeRes.customer.phone && (
            <a
              href={`tel:${activeRes.customer.phone}`}
              className="flex items-center gap-1 text-primary-600 hover:text-primary-700"
            >
              <Phone className="w-3 h-3" />
              {activeRes.customer.phone}
            </a>
          )}
        </div>
      </div>

      {/* Status badge */}
      <div className="px-3 py-2 flex items-center justify-center">
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium ${activeStatusBadgeColor.className}`}
          style={activeStatusBadgeColor.style}
        >
          {statusConfig?.name || activeRes.status}
        </span>
      </div>

      {/* Menú elegido: junto a las notas, para controlar los menús de cada
          mesa sin abrir la reserva (p.ej. la Feria: cuántos menús por mesa). */}
      {(activeRes.menuSelections?.length ?? 0) > 0 && (
        <div className="px-3 pb-2">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
            <p className="text-[11px] text-emerald-800 leading-snug">
              <span className="font-semibold">🍽️ Menú:</span>{" "}
              {activeRes
                .menuSelections!.map((s) => `${s.quantity}× ${s.menu.name}`)
                .join(" · ")}
            </p>
          </div>
        </div>
      )}

      {/* Notes / Observations */}
      {activeRes.notes && (
        <div className="px-3 pb-2">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
            <p className="text-[11px] text-amber-800 leading-snug line-clamp-3">
              <span className="font-semibold">Obs:</span> {activeRes.notes}
            </p>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="p-3 border-t border-gray-100 space-y-3">
        {/* Primary actions */}
        <div className="grid grid-cols-5 gap-1.5">
          <button
            onClick={() => onEditReservation(activeRes)}
            className="flex flex-col items-center justify-center gap-1 p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Edit3 className="w-4 h-4 text-primary-600" />
            <span className="text-[10px] font-medium text-gray-700">
              Editar
            </span>
          </button>
          <button
            onClick={() => onMoveTable(activeRes)}
            className="flex flex-col items-center justify-center gap-1 p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowRightLeft className="w-4 h-4 text-gray-600" />
            <span className="text-[10px] font-medium text-gray-700">Mover</span>
          </button>
          <button
            onClick={() => onReleaseTable(activeRes)}
            className="flex flex-col items-center justify-center gap-1 p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Unlock className="w-4 h-4 text-emerald-600" />
            <span className="text-[10px] font-medium text-gray-700">
              Liberar
            </span>
          </button>
          <button
            onClick={() => onBlockTable(table.id)}
            className="flex flex-col items-center justify-center gap-1 p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Lock className="w-4 h-4 text-red-500" />
            <span className="text-[10px] font-medium text-gray-700">
              Bloquear
            </span>
          </button>
          <button
            onClick={openCapacityForm}
            className="flex flex-col items-center justify-center gap-1 p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4 text-amber-600" />
            <span className="text-[10px] font-medium text-gray-700">
              Aforo
            </span>
          </button>
        </div>

        {/* Status change buttons */}
        <div className="space-y-1.5">
          <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
            Cambiar estado
          </p>
          <div className="flex flex-wrap gap-1.5">
            {availableStatuses
              .filter(
                (status) => !status.isFinal || status.code === activeRes.status,
              )
              .slice(0, 6)
              .map((status) => {
                const isActive = activeRes.status === status.code;
                const IconComponent = ICON_MAP[status.icon || ""] || Clock;
                const buttonColor = statusButtonColorProps(
                  status.bgColor,
                  status.textColor,
                  isActive,
                );

                return (
                  <button
                    key={status.id}
                    onClick={() => onChangeStatus(activeRes.id, status.code)}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${buttonColor.className}`}
                    style={buttonColor.style}
                  >
                    <IconComponent className="w-3 h-3" />
                    {status.name}
                  </button>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
